import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Form, Select, DatePicker, Input, Row, Col, Button, Space } from "antd";
import { QrcodeOutlined } from "@ant-design/icons";
import ShopSelect from "../common/ShopSelect";
import QrScanner, { parseShopCode } from "../common/QrScanner";
import dayjs from "dayjs";
import { createVisit, updateVisit, getUsers, listCameras, getTickets, listChecklistTemplates, identifyShop } from "../../network/api";
import { notify, serverError } from "../../utils/notify";
import { VISIT_TYPE, VISIT_TYPE_OPTIONS } from "../../constants/visits";

const { TextArea } = Input;

// Tickets still actionable enough to attach a corrective visit to.
const OPEN_TICKET_STATUSES = ["open", "assigned", "in_progress", "resolved"];

// `visit` (optional) switches the modal into edit mode — prefills the form and
// PUTs to /visits/:id instead of creating. Editing the core details (type,
// outlet, ticket, schedule, technician) is admin-only and works for ANY visit,
// regardless of who created or owns it.
const AddVisit = ({ visible, onClose, onSubmit, visit = null }) => {
  const isEdit = !!visit;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [shopId, setShopId] = useState();
  const [shopSeed, setShopSeed] = useState(); // populates ShopSelect's label after a scan
  const [scanOpen, setScanOpen] = useState(false);

  const onScan = async (raw) => {
    setScanOpen(false);
    try {
      const shop = await identifyShop(parseShopCode(raw));
      setShopId(shop._id);
      setShopSeed(shop);
      notify.success(`Outlet: ${shop.code} — ${shop.title}`);
    } catch (e) {
      notify.error(serverError(e, "No outlet matches that QR / code"));
    }
  };

  const [technicians, setTechnicians] = useState([]);
  const [cameras, setCameras] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [templates, setTemplates] = useState([]);
  const visitType = Form.useWatch("visitType", form);
  // Skip the "type changed → clear template" reset for the initial edit prefill.
  const skipTemplateReset = useRef(false);

  const loadStatics = useCallback(async () => {
    try {
      const [us, cams] = await Promise.all([
        getUsers({ role: "technician" }),
        listCameras({ page: 1, pageSize: 500 }),
      ]);
      setTechnicians((us.users || []).map((u) => ({ value: u._id, label: u.name })));
      setCameras((cams.data || []).map((c) => ({ value: c._id, label: `${c.deviceId}${c.deviceTitle ? ` — ${c.deviceTitle}` : ""}` })));
    } catch (e) {
      /* non-fatal */
    }
  }, []);

  useEffect(() => {
    if (visible) loadStatics();
  }, [visible, loadStatics]);

  // Edit mode: prefill the form from the existing visit when the modal opens.
  useEffect(() => {
    if (!visible || !isEdit) return;
    skipTemplateReset.current = true;
    form.setFieldsValue({
      visitType: visit.visitType,
      technician: visit.technician?._id || visit.technician || undefined,
      camera: visit.camera?._id || visit.camera || undefined,
      scheduledAt: visit.scheduledAt ? dayjs(visit.scheduledAt) : undefined,
      ticket: visit.ticket?._id || visit.ticket || undefined,
      checklistTemplate: visit.checklistTemplate?._id || visit.checklistTemplate || undefined,
      notes: visit.notes,
    });
    if (visit.shop) {
      const s = visit.shop;
      setShopId(s._id || s);
      if (s.code) setShopSeed(s);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, isEdit]);

  // Templates depend on the selected visit type; tickets only matter for corrective.
  useEffect(() => {
    if (!visitType) return;
    listChecklistTemplates(visitType)
      .then((t) => setTemplates(t.map((x) => ({ value: x._id, label: x.name }))))
      .catch(() => setTemplates([]));
    if (visitType === VISIT_TYPE.CORRECTIVE) {
      getTickets()
        .then((r) => setTickets((r.tickets || []).filter((t) => OPEN_TICKET_STATUSES.includes(t.status)).map((t) => ({ value: t._id, label: `${t.title} (${t.deviceId || "—"})` }))))
        .catch(() => setTickets([]));
    }
    // Preserve the prefilled template on the initial edit load; clear only on a
    // user-initiated type change.
    if (skipTemplateReset.current) {
      skipTemplateReset.current = false;
    } else {
      form.setFieldsValue({ checklistTemplate: undefined });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitType]);

  const close = () => {
    form.resetFields();
    setShopId(undefined);
    setShopSeed(undefined);
    onClose();
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const payload = { ...values, shop: shopId || null };
      if (isEdit) {
        await updateVisit(visit._id, payload);
        notify.success("Visit updated");
      } else {
        await createVisit(payload);
        notify.success("Visit created");
      }
      close();
      onSubmit();
    } catch (error) {
      if (error?.errorFields) {
        setLoading(false);
        return;
      }
      notify.error(serverError(error, isEdit ? "Failed to update visit" : "Failed to create visit"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <Modal title={isEdit ? "Edit Visit" : "New Visit"} open={visible} onOk={handleOk} confirmLoading={loading} okText={isEdit ? "Save" : "Create"} onCancel={close} width={560}>
      <Form form={form} layout="vertical" requiredMark={false}>
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="visitType" label="Visit type" rules={[{ required: true, message: "Select a type" }]}>
              <Select placeholder="Select type" options={VISIT_TYPE_OPTIONS} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="technician" label="Technician">
              <Select allowClear showSearch placeholder="Assign technician" optionFilterProp="label" options={technicians} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item label="Outlet">
          <Space.Compact style={{ width: "100%" }}>
            <ShopSelect allowClear value={shopId} onChange={setShopId} current={shopSeed} placeholder="Search outlets…" />
            <Button icon={<QrcodeOutlined />} onClick={() => setScanOpen(true)}>Scan</Button>
          </Space.Compact>
        </Form.Item>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="camera" label="Device">
              <Select allowClear showSearch placeholder="Select device" optionFilterProp="label" options={cameras} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item name="scheduledAt" label="Scheduled for">
              <DatePicker showTime style={{ width: "100%" }} format="YYYY-MM-DD HH:mm" />
            </Form.Item>
          </Col>
        </Row>

        {visitType === VISIT_TYPE.CORRECTIVE && (
          <Form.Item name="ticket" label="Linked ticket" extra="Completing the visit will resolve this ticket.">
            <Select allowClear showSearch placeholder="Select an open ticket" optionFilterProp="label" options={tickets} />
          </Form.Item>
        )}

        <Form.Item name="checklistTemplate" label="Checklist template">
          <Select
            allowClear
            placeholder={visitType ? "Select a checklist" : "Pick a visit type first"}
            disabled={!visitType}
            options={templates}
            notFoundContent="No templates for this type"
          />
        </Form.Item>

        <Form.Item name="notes" label="Notes">
          <TextArea rows={2} placeholder="Optional instructions for the technician" />
        </Form.Item>
      </Form>
    </Modal>
    <QrScanner open={scanOpen} onClose={() => setScanOpen(false)} onResult={onScan} title="Scan outlet QR" />
    </>
  );
};

export default AddVisit;
